import { memo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { fetchSegments } from "@/store/slices/segments/segments.thunk";
import { fetchAttributeLabels } from "@/store/slices/attributeLabels/attributeLabels.thunk";
import {
  markFiltersReady,
  markSegmentsReady,
  setPendingValidationSegments,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { SEGMENTS } from "@/constant/constantsThrougoutProject";

/**
 * FetchFiltersAndSegments
 *
 * Renderless orchestrator for the v2 filters/segments catalogs:
 *
 *  1. **Segments catalog fetch** — `fetchSegments()` fires exactly once per
 *     session (ref-guarded against React 18 StrictMode double-mount). The v2
 *     endpoint is market-agnostic; per-market views are derived locally via
 *     selectors, so there is no per-market re-fetch anymore.
 *
 *  2. **Shared-segment URL capture** — once the initial fetch resolves, any
 *     `segments` IDs present in the URL are handed to `pendingValidationSegments`
 *     for `ValidateSegment` to resolve against the fresh catalog. v2 segments
 *     are keyed by string `code`, not the legacy numeric `id`, so this
 *     component can no longer pre-filter against "segments the user already
 *     owns" — that check now lives entirely in `ValidateSegment`.
 *
 *  3. **Readiness flags** — `markFiltersReady()` / `markSegmentsReady()` fire
 *     whenever the corresponding catalog slice is `initialized` but the
 *     `pendingSelection` ready flag is `false`. This covers both the initial
 *     load AND the atomic `filtersReady:false, segmentsReady:false` reset
 *     that `MarketsDropdownView` dispatches on every market change — since
 *     neither catalog needs re-fetching, the flag is simply flipped back so
 *     `selectionListener`'s prune-cycle guard isn't left blocked.
 *
 *  4. **Attribute labels** — the one genuinely per-market fetch; re-runs
 *     whenever the committed `userSelection.markets` changes.
 */
const FetchFiltersAndSegments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const markets = useSelector((state: RootState) => state.userSelection.markets);
  const filtersInitialized = useSelector(
    (state: RootState) => state.activeGroup.filters.initialized,
  );
  const segmentsInitialized = useSelector(
    (state: RootState) => state.activeGroup.segments.initialized,
  );
  const filtersReady = useSelector(
    (state: RootState) => state.pendingSelection.filtersReady,
  );
  const segmentsReady = useSelector(
    (state: RootState) => state.pendingSelection.segmentsReady,
  );

  const segmentsFetchedRef = useRef(false);

  // Filters catalog arrives via bootstrap. Flip the ready flag whenever it's
  // hydrated but the flag is currently false (initial load, or a market
  // change that reset it).
  useEffect(() => {
    if (filtersInitialized && !filtersReady) dispatch(markFiltersReady());
  }, [filtersInitialized, filtersReady, dispatch]);

  // Fetch the segments catalog exactly once per session.
  useEffect(() => {
    if (segmentsFetchedRef.current) return;
    segmentsFetchedRef.current = true;

    dispatch(fetchSegments()).then((result) => {
      if (!fetchSegments.fulfilled.match(result)) return;

      const urlSegments = new URLSearchParams(window.location.search)
        .getAll(SEGMENTS)
        .map(Number)
        .filter(Boolean);
      if (urlSegments.length > 0) {
        dispatch(setPendingValidationSegments(urlSegments));
      }
    });
  }, [dispatch]);

  // Mirror of the filters effect above, for the segments catalog.
  useEffect(() => {
    if (segmentsInitialized && !segmentsReady) dispatch(markSegmentsReady());
  }, [segmentsInitialized, segmentsReady, dispatch]);

  // Attribute labels remain the one per-market-change fetch.
  useEffect(() => {
    if (markets.length === 0) return;
    dispatch(fetchAttributeLabels({ marketIds: markets }));
  }, [markets, dispatch]);

  return null;
};

export default memo(FetchFiltersAndSegments);
